import { HUD } from '../../config/tuning';
import type { HudPainter } from '../HudLayout';
import type { HorizonGeometry } from './Horizon';

/**
 * The pitch ladder (SPEC §3.4.1): dashed segments every 5°, positive solid and negative dashed,
 * with small end ticks angled toward the horizon. Rotates and translates with the horizon bar.
 *
 * Rung placement is exact rather than linearised. A HUD that spaces rungs by `angle × pixelsPerRad`
 * is right near the horizon and increasingly wrong toward the vertical, which shows up as the
 * ladder disagreeing with the terrain exactly when the player is in a steep dive and needs it.
 * The tangent form below costs one extra call per rung and is correct everywhere.
 */

const DEG_TO_RAD = Math.PI / 180;

/**
 * Screen offset of a rung at `elevationRad`, in the horizon's rotated frame, pixels.
 * The horizon itself is the elevation-0 case and comes out as `horizon.offsetPx`.
 */
export function ladderRungOffsetPx(
  horizon: HorizonGeometry,
  elevationRad: number,
  focalPx: number,
): number {
  const cameraElevationRad = Math.atan(horizon.offsetPx / focalPx);
  return -focalPx * Math.tan(elevationRad - cameraElevationRad);
}

export function drawPitchLadder(
  painter: HudPainter,
  horizon: HorizonGeometry,
  focalPx: number,
): void {
  if (!horizon.valid) return;

  const layout = painter.layout;
  const ctx = painter.ctx;
  const halfWidth = layout.px(HUD.ladderRungHalfWidthPx);
  const gap = layout.px(HUD.ladderRungGapPx) * 0.5;
  const dash = layout.px(HUD.ladderDashPx);
  const tick = layout.px(HUD.ladderEndTickPx);
  const labelOffset = layout.px(HUD.ladderLabelOffsetPx);

  // A rung is worth drawing only if it lands on the display; the diagonal bounds every rotation.
  const limitPx = Math.hypot(layout.widthPx, layout.heightPx) * 0.5 + halfWidth;

  ctx.save();
  ctx.translate(layout.centreX, layout.centreY);
  ctx.rotate(horizon.rollRad);

  for (
    let degrees = -HUD.ladderMaxPitchDeg;
    degrees <= HUD.ladderMaxPitchDeg;
    degrees += HUD.ladderStepDeg
  ) {
    if (degrees === 0) continue; // the horizon bar is the zero rung
    const y = ladderRungOffsetPx(horizon, degrees * DEG_TO_RAD, focalPx);
    if (!Number.isFinite(y) || Math.abs(y) > limitPx) continue;

    // End ticks point toward the horizon: down from a climb rung, up from a dive rung.
    const tickDirection = degrees > 0 ? 1 : -1;
    const climbing = degrees > 0;

    ctx.beginPath();
    ctx.moveTo(-halfWidth, y);
    ctx.lineTo(-gap, y);
    ctx.moveTo(gap, y);
    ctx.lineTo(halfWidth, y);
    if (climbing) {
      painter.stroke();
    } else {
      DASH_PATTERN[0] = dash;
      DASH_PATTERN[1] = dash;
      ctx.setLineDash(DASH_PATTERN);
      painter.stroke();
      ctx.setLineDash(NO_DASH);
    }

    ctx.beginPath();
    ctx.moveTo(-gap, y);
    ctx.lineTo(-gap, y + tick * tickDirection);
    ctx.moveTo(gap, y);
    ctx.lineTo(gap, y + tick * tickDirection);
    painter.stroke();

    // Rung labels are a fixed set, so they are built once rather than formatted every frame.
    const label = rungLabel(Math.abs(degrees));
    painter.text(label, halfWidth + labelOffset, y, 'left');
    painter.text(label, -halfWidth - labelOffset, y, 'right');
  }

  ctx.restore();
}

const DASH_PATTERN = [0, 0];
const NO_DASH: number[] = [];

const RUNG_LABELS = new Map<number, string>();
function rungLabel(degrees: number): string {
  let label = RUNG_LABELS.get(degrees);
  if (label === undefined) {
    label = degrees.toFixed(0);
    RUNG_LABELS.set(degrees, label);
  }
  return label;
}
